import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { addEvent, removeEvent, offset, width } from '../../../utils/dom';
import s from './RangeSlide.scss';

const RangeSlide = ({ labels, onChange }) => {
	const controls = useRef(null);
	const interactingWithControl = useRef(false);
	const [lowerThreshold, updateLowerThreshold] = useState(0);
	const [upperThreshold, updateUpperThreshold] = useState(100);

	const getDragX = (e) => {
		if (e.clientX) return e.clientX;
		return e?.touches?.[0]?.pageX;
	};

	useEffect(() => {
		let debounceTimer = null;

		const moveEvent = (e) => {
			window.requestAnimationFrame(() => {
				if (interactingWithControl.current) {
					const x = updateControlPosition(e);
					const currentPositions = [lowerThreshold, upperThreshold];

					if (interactingWithControl.current === 'lower') {
						updateLowerThreshold(x);
						currentPositions[0] = x;
					} else if (interactingWithControl.current === 'upper') {
						updateUpperThreshold(x);
						currentPositions[1] = x;
					}

					window.clearTimeout(debounceTimer);
					debounceTimer = window.setTimeout(() => {
						onChange([...controls.current.querySelectorAll('button')].map((b) => parseInt(b.style.left, 10)));
					}, 300);
				}
			});
		};

		const endEvent = (e) => {
			window.requestAnimationFrame(() => {
				if (interactingWithControl.current) {
					const x = updateControlPosition(e);
					const currentPositions = [lowerThreshold, upperThreshold];

					if (interactingWithControl.current === 'lower') {
						updateLowerThreshold(x);
						currentPositions[0] = x;
					} else if (interactingWithControl.current === 'upper') {
						updateUpperThreshold(x);
						currentPositions[1] = x;
					}

					onChange([...controls.current.querySelectorAll('button')].map((b) => parseInt(b.style.left, 10)));

					window.clearTimeout(debounceTimer);
					interactingWithControl.current = false;
				}
			});
		};

		addEvent(window, 'mousemove.rangeSlide', moveEvent);
		addEvent(window, 'touchmove.rangeSlide', moveEvent);

		addEvent(window, 'mouseup.rangeSlide', endEvent);
		addEvent(window, 'touchend.rangeSlide', endEvent);

		return () => {
			interactingWithControl.current = false;
			removeEvent(window, 'mousemove.rangeSlide');
			removeEvent(window, 'mouseup.rangeSlide');
			removeEvent(window, 'touchmove.rangeSlide');
			removeEvent(window, 'touchend.rangeSlide');
		};
	}, []);

	const updateControlPosition = (e) => {
		if (!controls.current) return null;

		const { left } = offset(controls.current);
		const w = width(controls.current);
		let x = Math.round(((getDragX(e) - left) / w) * 1000) / 10;

		if (x > 100) x = 100;
		if (x < 0) x = 0;

		return x;
	};

	const handleMouseDown = (e) => {
		e.preventDefault();
		interactingWithControl.current = e.currentTarget.dataset.control;
	};

	return (
		<div className={s.rangeSlide} ref={controls}>
			<div className={s.rangeSlide_inner}>
				<button className={s.rangeSlide_inner_control} data-control="lower" onMouseDown={handleMouseDown} onTouchStart={handleMouseDown} type="button" aria-label="Restrict lower threshold" style={{ left: `${lowerThreshold}%` }} />
				{ labels.map(([p, t]) => <span key={p} className={s.rangeSlide_inner_point} style={{ left: `${p}%` }}>{ t }</span>) }
				<button className={s.rangeSlide_inner_control} data-control="upper" onMouseDown={handleMouseDown} onTouchStart={handleMouseDown} type="button" aria-label="Restrict upper threshold" style={{ left: `${upperThreshold}%` }} />
			</div>
		</div>
	);
};

export default hot(module)(RangeSlide);
